import {
  BackSide,
  CanvasTexture,
  EquirectangularReflectionMapping,
  Mesh,
  MeshBasicMaterial,
  PMREMGenerator,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  WebGLRenderer,
} from 'three';
import { seeded } from './materials';

/**
 * Night sky.
 *
 * Does two jobs, and the second one matters more than it looks:
 *
 *  1. Gives us an actual moon and a graded horizon to see through the atrium
 *     canopy and the parking structure openings (reference panels 1, 5, 7).
 *  2. Feeds a PMREM environment map into the scene. Without an environment,
 *     `MeshStandardMaterial` has nothing to reflect and polished mall tile reads
 *     as flat grey paint. The wet-looking floor in panels 2 and 5 is almost
 *     entirely an environment reflection, so this is not optional dressing.
 */

export interface SkyOptions {
  /** Where the moon sits in the sky texture, as [azimuth 0..1, elevation 0..1]. */
  moonUV?: [number, number];
  moonRadiusPx?: number;
  /** Overall brightness multiplier for the environment contribution. */
  exposure?: number;
}

/**
 * Convert a directional light's position into the sky texture coordinate where
 * its source should be painted.
 *
 * Authoring these by hand is how the painted moon ends up in one corner of the
 * sky while the shadows fall as though it were in the other. There is exactly
 * one moon, so there is exactly one place this number can come from.
 *
 * The mapping matches `SphereGeometry`'s own UV layout — v = 1 at the zenith,
 * and the canvas is drawn top-down, so the polar angle maps straight to canvas
 * Y with no flip.
 */
export function moonUVFromDirection(dir: readonly [number, number, number]): [number, number] {
  const [x, y, z] = dir;
  const len = Math.hypot(x, y, z) || 1;
  const polar = Math.acos(Math.max(-1, Math.min(1, y / len)));
  const azimuth = Math.atan2(z, -x);
  const u = ((azimuth / (Math.PI * 2)) % 1 + 1) % 1;
  return [u, polar / Math.PI];
}

function makeSkyTexture(opts: SkyOptions): CanvasTexture {
  const W = 2048;
  const H = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  const exposure = opts.exposure ?? 1;
  const c = (r: number, g: number, b: number) =>
    `rgb(${Math.round(Math.min(255, r * exposure))},${Math.round(Math.min(255, g * exposure))},${Math.round(Math.min(255, b * exposure))})`;

  // Vertical gradient: deep blue zenith, slightly warmer haze at the horizon from
  // distant city sodium. The horizon lift is what stops exteriors reading as a void.
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0.0, c(10, 16, 28));
  grad.addColorStop(0.42, c(18, 27, 42));
  grad.addColorStop(0.52, c(30, 40, 55));
  grad.addColorStop(0.58, c(42, 44, 48));
  grad.addColorStop(1.0, c(12, 13, 15));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Cloud banding — a few soft horizontal smears so the sky is not a clean ramp.
  ctx.globalAlpha = 0.16;
  // Fixed seed: a night sky that reshuffles itself on every page load makes two
  // capture runs of the same commit differ. See `seeded`.
  const rnd = seeded(90210);
  for (let i = 0; i < 26; i++) {
    const y = rnd() * H * 0.5;
    const h = 12 + rnd() * 70;
    const g2 = ctx.createLinearGradient(0, y, 0, y + h);
    g2.addColorStop(0, 'rgba(120,140,175,0)');
    g2.addColorStop(0.5, 'rgba(120,140,175,0.5)');
    g2.addColorStop(1, 'rgba(120,140,175,0)');
    ctx.fillStyle = g2;
    ctx.fillRect(0, y, W, h);
  }
  ctx.globalAlpha = 1;

  // Stars, thinning toward the horizon haze.
  for (let i = 0; i < 900; i++) {
    const x = rnd() * W;
    const y = rnd() * H * 0.46;
    const fade = 1 - y / (H * 0.46);
    const a = rnd() * 0.5 * fade;
    ctx.fillStyle = `rgba(200,215,240,${a.toFixed(3)})`;
    const s = rnd() < 0.06 ? 2 : 1;
    ctx.fillRect(x, y, s, s);
  }

  // The moon. Drawn with a wide halo because the halo is what actually reads
  // through fog and glass at distance.
  const [mu, mv] = opts.moonUV ?? [0.3, 0.24];
  const mx = mu * W;
  const my = mv * H;
  const r = opts.moonRadiusPx ?? 34;

  const halo = ctx.createRadialGradient(mx, my, r * 0.6, mx, my, r * 11);
  halo.addColorStop(0, 'rgba(190,210,240,0.42)');
  halo.addColorStop(0.25, 'rgba(150,175,215,0.14)');
  halo.addColorStop(1, 'rgba(120,150,200,0)');
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(mx, my, r * 11, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = 'rgb(236,240,248)';
  ctx.beginPath();
  ctx.arc(mx, my, r, 0, Math.PI * 2);
  ctx.fill();

  // Maria — a few darker patches so it is a moon, not a white dot.
  ctx.fillStyle = 'rgba(178,190,208,0.75)';
  for (const [ox, oy, or] of [
    [-0.3, -0.2, 0.26],
    [0.22, 0.14, 0.2],
    [-0.05, 0.36, 0.15],
    [0.36, -0.34, 0.12],
  ]) {
    ctx.beginPath();
    ctx.arc(mx + ox * r, my + oy * r, or * r, 0, Math.PI * 2);
    ctx.fill();
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.mapping = EquirectangularReflectionMapping;
  tex.needsUpdate = true;
  return tex;
}

export interface SkyHandle {
  /** Visible sky dome. Kept as real geometry so fog affects it like everything else. */
  dome: Mesh;
  dispose(): void;
}

/**
 * Installs sky background + environment on a scene. Returns a handle so a level
 * can swap or remove the dome (interior-only levels do not need it).
 */
export function installSky(scene: Scene, renderer: WebGLRenderer, opts: SkyOptions = {}): SkyHandle {
  const tex = makeSkyTexture(opts);

  // Environment map drives all reflections. Generated once at load.
  const pmrem = new PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  const envRT = pmrem.fromEquirectangular(tex);
  scene.environment = envRT.texture;
  // Low on purpose. An environment map is not just a reflection — three feeds it
  // to every standard material as diffuse irradiance, and it does not care about
  // occlusion. At 0.55 the sky lights the floor of a sealed control room three
  // storeys inside the building, and because up-facing normals catch the bright
  // part of the sphere while walls catch the dark part, every room in the game
  // came out with a blown-white floor and black walls. Keep it just high enough
  // to put a specular sheen on polished tile; the floor's brightness has to come
  // from fittings in the room.
  scene.environmentIntensity = 0.16;
  pmrem.dispose();

  // A large inward-facing sphere rather than `scene.background`, so the sky is
  // real geometry we can occlude, tint per-level, and render behind everything.
  // Fog is disabled on it — at 900m any exponential fog would erase it entirely;
  // the horizon instead blends because the texture's horizon band is authored to
  // match the fog colour.
  const domeMat = new MeshBasicMaterial({
    map: tex,
    side: BackSide,
    fog: false,
    depthWrite: false,
    toneMapped: true,
  });
  const dome = new Mesh(new SphereGeometry(900, 32, 20), domeMat);
  dome.renderOrder = -1000;
  scene.add(dome);

  return {
    dome,
    dispose() {
      scene.remove(dome);
      dome.geometry.dispose();
      envRT.dispose();
      tex.dispose();
      scene.environment = null;
    },
  };
}
